// -------------------------------
// 🔹 Imports externes (librairies tierces)
// -------------------------------
import { useMutation } from '@tanstack/vue-query';
import type { AxiosError } from 'axios';

// -------------------------------
// 🔹 Imports internes — Références réactives (refs globales)
// -------------------------------
import { isGlobalError, GlobalErrorMessage } from '@/refs/common.refs';
import { isLoaderLoginFormActivated } from '@/refs/login.form.refs';

// -------------------------------
// 🔹 Imports internes — Stores Pinia
// -------------------------------
import { useUserStore } from '@/store/user.store';

// -------------------------------
// 🔹 Imports internes — Types TypeScript
// -------------------------------
import type {
  IBodyUserCustomer,
  IRoleBody,
  IUpdateStaffRole,
} from '@/types/user.type';
import { UserService } from '@/services/user.service';
import { useTaxeStore } from '@/store/taxe.stsore';
import type { IBodyTax, IBodyTaxUpdate } from '@/types/taxe.types';

/**
 * Hook pour gérer les mutations liées aux users.
 * Fournit des fonctions pour créer, mettre à jour et supprimer des users
 * avec gestion automatique des erreurs globales et du loader.
 */
export const TaxeMutation = () => {
  const userStore = useUserStore();
  const taxeStore = useTaxeStore();

  // -------------------------------
  // 🔹 Gestion centralisée des erreurs
  // -------------------------------
  const handleError = (err: any) => {
    const error = err as AxiosError;
    const response = error.response?.data as any;
    const message = response?.message?.[0] || 'Une erreur est survenue';

    isGlobalError.value = true;
    GlobalErrorMessage.value = message;
    setTimeout(() => {
      isGlobalError.value = false;
      isLoaderLoginFormActivated.value = false;
    }, 1000);
  };

  // -------------------------------
  // 🔹 Création d'un taxe
  // -------------------------------
  const createTaxe = useMutation<any, Error, IBodyTax>({
    mutationFn: (payload: IBodyTax) => {
      return taxeStore.createTaxe(payload);
    },
    onSuccess: async (data) => {},
    onError: handleError,
  });

  // -------------------------------
  // 🔹 Mise à jour d'un taxe
  // -------------------------------
  const updateTaxById = useMutation<any, Error, IBodyTaxUpdate>({
    mutationFn: (payload: IBodyTaxUpdate) => {
      return taxeStore.updateTaxById(payload);
    },
    onSuccess: async (data) => {},
    onError: handleError,
  });

  // -------------------------------
  // 🔹 Suppression d'un taxe
  // -------------------------------
  const deleteTaxById = useMutation<any, Error>({
    mutationFn: () => taxeStore.deleteTaxById(),
    onSuccess: () => {},
    onError: handleError,
  });

  /**
   * Crée une nouvelle taxe
   * @param {IBodyTax} item - Données du taxe
   */
  const handleCreateTaxe = (item: IBodyTax) => {
    return createTaxe.mutateAsync(item);
  };
  /**
   * Met à jour un taxe existant
   * @param {IUpdateStaffRole} item - Données de mise à jour du taxe
   */
  const handleUpdateTaxById = (item: IBodyTaxUpdate) => {
    return updateTaxById.mutateAsync(item);
  };
  /**
   * Suppression d'un taxe existant
   */
  const handleDeleteTaxById = () => {
    return deleteTaxById.mutateAsync();
  };

  return {
    handleCreateTaxe,
    handleUpdateTaxById,
    handleDeleteTaxById,
  };
};
